import { useTranslation } from 'react-i18next';
import { clinicStatusesDataSource } from 'infrastructure/data-sources/clinic-statuses';
import OpenIcon from 'assets/img/icons/box-arrow-up-right.svg?react';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'router/routes';
import { formatRoleName } from 'utils/formatters';
import { Roles } from 'infrastructure/enums';

import s from './styles.module.scss';

import type { IClinicCareManager } from 'infrastructure/interfaces';
import type { IBaseTableColumnProps } from 'components/atoms/base-table/types';

const displayedRoles = [
  Roles.PhysicianAssistant,
  Roles.QHCP,
  Roles.RegisteredNurse,
  Roles.MedicalAssistant,
  Roles.NursePractitioner,
  Roles.Doctor,
];

function prepareRolesToDisplay(roles: Roles[]): string {
  return roles
    .filter((role) => displayedRoles.includes(role))
    .map(formatRoleName)
    .join(', ');
}

interface IProps {
  disableRefs?: boolean;
  isEditable?: boolean;
}

export const useCareManagersCols = ({ disableRefs, isEditable }: IProps) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  let cols: IBaseTableColumnProps<IClinicCareManager>[] = [];
  if (isEditable) {
    cols = [
      {
        label: t('labels.user'),
        key: 'firstName',
        width: '100%',
        render(val, record) {
          return (
            <div className={s['name-wrapper-editable']}>
              <button
                className={s['navigation-wrapper']}
                style={{ cursor: disableRefs ? 'default' : 'pointer' }}
                onClick={() => {
                  if (!disableRefs) {
                    navigation(Routes.adminAgencyDoctor(record.doctorGuid));
                  }
                }}
              >
                <OpenIcon width={16} height={16} />
              </button>
              <p className={s['text-bold']}>
                {record.firstName} {record.lastName}
              </p>
            </div>
          );
        },
      },
    ];
  } else {
    cols = [
      {
        label: t('labels.user'),
        key: 'firstName',
        width: '20%',
        render(val, record) {
          return (
            <div className={s['name-wrapper']}>
              <button
                className={s['navigation-wrapper']}
                style={{ cursor: disableRefs ? 'default' : 'pointer' }}
                onClick={() => {
                  if (!disableRefs) {
                    navigation(Routes.adminAgencyDoctor(record.doctorGuid));
                  }
                }}
              >
                <OpenIcon width={16} height={16} />
              </button>
              <p className={s['text-bold']}>
                {record.firstName} {record.lastName}
              </p>
            </div>
          );
        },
      },
      {
        label: t('labels.email'),
        key: 'email',
        render(val) {
          return <p>{val}</p>;
        },
      },
      {
        label: t('labels.agency'),
        key: 'agencies',
        render(val, record) {
          return <p>{record.agencies.join(', ')}</p>;
        },
      },
      {
        label: t('labels.role'),
        key: 'roles',
        render(val) {
          return <p>{prepareRolesToDisplay(val)}</p>;
        },
      },
      {
        label: t('labels.status'),
        key: 'status',
        render(val, record) {
          const status = clinicStatusesDataSource().findByField(
            'id',
            record.status,
          );
          return (
            <p
              data-cy="clinicListTable-status"
              className={s['text-bold']}
              style={{ color: status?.color }}
            >
              {status?.name}
            </p>
          );
        },
      },
    ];
  }

  return { cols };
};
